import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Button, Alert, Row, Col, Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { getImageFrameStyle, getImageTagStyle } from '../constants/imageDisplay';

function AdminFinalReviewScreen() {
    const { jobId } = useParams();
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const [job, setJob] = useState(null);
    const [images, setImages] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imagesError, setImagesError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);

    const authConfig = () => ({ headers: { Authorization: `Bearer ${userInfo.token}` } });

    const loadJob = () => axios.get(`/api/genai/admin/jobs/${jobId}/`, authConfig()).then(res => setJob(res.data));

    const loadImages = () => axios.get(`/api/genai/admin/jobs/${jobId}/images/`, authConfig())
        .then(res => setImages(res.data || {}))
        .catch(err => setImagesError(err.response?.data?.error || err.message));

    useEffect(() => {
        if (!userInfo || !userInfo.isAdmin) {
            navigate('/login');
            return;
        }
        setLoading(true);
        Promise.all([loadJob(), loadImages()])
            .catch(err => setError(err.response?.data?.error || err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, navigate, userInfo]);

    // Poll while PDF is still assembling
    useEffect(() => {
        if (!job || job.status !== 'assembling') return;
        const interval = setInterval(() => { loadJob(); loadImages(); }, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.status]);

    const handleApproveFinal = () => {
        setActionLoading(true);
        setActionError(null);
        axios.post(`/api/genai/jobs/${jobId}/approve-final/`, {}, authConfig())
            .then(() => navigate(`/admin/genai/jobs/${jobId}`))
            .catch(err => setActionError(err.response?.data?.error || err.message))
            .finally(() => setActionLoading(false));
    };

    const handleDownloadPrintPDF = async () => {
        try {
            const response = await fetch(`/api/genai/admin/jobs/${jobId}/download-print-pdf/`, authConfig());
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setActionError(data.error || 'Failed to download print PDF');
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `book_${jobId}_print.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setActionError(err.message || 'Failed to download print PDF');
        }
    };

    if (loading) return <Loader />;
    if (error) return <Message variant="danger">{error}</Message>;
    if (!job) return null;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Staff QA — Final Book</h1>
                <Link to={`/admin/genai/jobs/${jobId}`}>Back to job detail</Link>
            </div>

            <Alert variant="info">Customer chose "TaleTots reviews for me". Job {jobId}</Alert>
            {actionError && <Message variant="danger">{actionError}</Message>}
            {imagesError && <Message variant="danger">{imagesError}</Message>}

            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Final Book Preview</strong>
                    <Button variant="outline-secondary" size="sm" onClick={handleDownloadPrintPDF} disabled={!job.pdf_url}>
                        Download Print PDF
                    </Button>
                </Card.Header>
                <Card.Body>
                    {Object.keys(images).length === 0 ? (
                        <p className="text-muted mb-0">No final pages are available yet.</p>
                    ) : (
                        <Row>
                            {Object.entries(images)
                                .sort(([a], [b]) => Number(a) - Number(b))
                                .map(([pageNum, image]) => (
                                    <Col key={pageNum} md={6} lg={4} className="mb-4">
                                        <div className="small fw-semibold mb-2">
                                            {pageNum === '0' ? 'Front Cover' : pageNum === '13' ? 'Back Cover' : `Page ${pageNum}`}
                                        </div>
                                        <div style={getImageFrameStyle(image, pageNum, { maxHeightCover: 320, maxHeightStory: 260 })}>
                                            <Image
                                                src={image.url || image.image_url}
                                                alt={`Page ${pageNum}`}
                                                fluid
                                                className="border rounded"
                                                style={getImageTagStyle()}
                                            />
                                        </div>
                                    </Col>
                                ))}
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {job.status === 'assembling' ? (
                <div className="text-center py-5 text-muted"><Loader /><p className="mt-3">PDF is still assembling…</p></div>
            ) : job.status === 'completed' ? (
                <Alert variant="success">This book has already been approved and released to the customer.</Alert>
            ) : (
                <Card className="mb-4 p-4 bg-light border-success" style={{ borderWidth: '2px' }}>
                    <h4 className="mb-2">Ready for final approval</h4>
                    <p className="mb-3 text-muted">
                        Approving releases the completed book and sends the customer their digital-product email.
                    </p>
                    <div>
                        <Button variant="success" size="lg" onClick={handleApproveFinal} disabled={actionLoading || !job.pdf_url}>
                            {actionLoading ? 'Approving…' : 'Approve & Release to Customer'}
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default AdminFinalReviewScreen;
